import { isSheet } from '../lib/keys'

/** Native workspace protocol v1. Transcript and terminal state never cross this boundary. */
export const MAX_TABS = 1_000
export const MAX_DRAFTS = 1_000
const MAX_SAVED_TABS = 20
export const MAX_PATH_BYTES = 2_000
export const MAX_DRAFT_BYTES = 256 * 1024
export const MAX_RECOVERY_BYTES = 8 * 1024 * 1024
export interface RecoveryDraft { tab: string; path: string; key: string; value: string; dirty: boolean }
export interface WorkspaceRecovery { version: 1; identity: string; state: WorkspaceState; drafts: RecoveryDraft[] }
export interface SavedTabs {
  version: 1
  panes: { tabs: string[]; front: number }[]
  inUse: number
  share: number | null
}
export type WorkspaceCommand =
  | { type: 'open'; path: string; disposition: 'external' | 'current' | 'background' | 'foreground' | 'other-pane'; source?: string }
  | { type: 'new-tab' | 'split' }
  | { type: 'close-tab' | 'back' | 'forward' | 'reload' | 'settings'; source?: string }
  | { type: 'move-other'; tab?: string }
  | { type: 'cycle'; step: number }
export interface WorkspaceState {
  version: 1
  context: number
  panes: { tabs: { id: string; path: string; title: string; unsaved: boolean; blank: boolean; canGoBack: boolean; canGoForward: boolean }[]; front: string }[]
  inUse: number
  share: number | null
}
export function inWorkspace() {
  return nativeBootstrap() !== null || document.documentElement?.dataset.workspace === '1'
}
/** Native document-start injection precedes the HTML root on some WebKit loads. */
export function nativeBootstrap(): string | null {
  const value = (window as unknown as { __poulpeWorkspaceBootstrap?: unknown }).__poulpeWorkspaceBootstrap
  return typeof value === 'string' ? value : null
}
const record = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const id = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(value)
/** What the server answers itself (crates/poulpe-server/src/http.rs): never a page. */
const SERVER_ROUTE = /^\/(v1|mcp|health)(\/|$)/i
const pathShape = (text: string) => text.startsWith('/') && !text.startsWith('//') && !/[\\\x00-\x1f\x7f]/.test(text)

/** Match SavedTabs.page: root relative, bounded, and never one of the server's own routes (its API, MCP, health). */
export function safePath(value: unknown): string | null {
  if (typeof value !== 'string' || !pathShape(value) || new TextEncoder().encode(value).length > MAX_PATH_BYTES) return null
  try {
    const url = new URL(value, 'http://workspace.invalid')
    if (url.origin !== 'http://workspace.invalid') return null
    // Decode after URL normalisation too: encoded letters/slashes cannot hide an action route.
    const path = decodeURIComponent(url.pathname)
    if (!pathShape(path) || /(?:^|\/)\.\.(?:\/|$)/.test(path) || SERVER_ROUTE.test(path)) return null
    const reference = url.pathname + url.search + url.hash
    return new TextEncoder().encode(reference).length <= MAX_PATH_BYTES ? reference : null
  } catch { return null }
}
export function bootstrap(text: string | null): SavedTabs | null {
  if (!text || text.length > MAX_RECOVERY_BYTES) return null
  try {
    const value: unknown = JSON.parse(text)
    if (!record(value) || value.version !== 1 || !record(value.saved)) return null
    const saved = value.saved
    if (saved.version !== 1 || !Array.isArray(saved.panes) || saved.panes.length < 1 || saved.panes.length > 2) return null
    if (!Number.isSafeInteger(saved.inUse) || (saved.share !== undefined && saved.share !== null && typeof saved.share !== 'number')) return null
    const panes: SavedTabs['panes'] = []
    let inUse = 0
    for (const [index, pane] of saved.panes.entries()) {
      if (!record(pane) || !Array.isArray(pane.tabs) || !pane.tabs.every(path => typeof path === 'string') || !Number.isSafeInteger(pane.front)) return null
      let tabs: string[] = []
      let front: number | undefined
      for (const [at, address] of pane.tabs.entries()) {
        const path = safePath(address)
        if (!path) continue
        if (at >= (pane.front as number) && front === undefined) front = tabs.length
        tabs.push(path)
      }
      if (!tabs.length) continue
      front ??= tabs.length - 1
      if (tabs.length > MAX_SAVED_TABS) {
        const selected = tabs[front]!
        tabs = tabs.slice(0, MAX_SAVED_TABS)
        if (front >= MAX_SAVED_TABS) { front = MAX_SAVED_TABS - 1; tabs[front] = selected }
      }
      if (index === saved.inUse) inUse = panes.length
      panes.push({ tabs, front })
    }
    if (!panes.length) return null
    const share = panes.length === 2 && typeof saved.share === 'number' && Number.isFinite(saved.share)
      ? Math.round(Math.min(.9, Math.max(.1, saved.share)) * 1000) / 1000 : null
    return { version: 1, panes, inUse, share }
  } catch { return null }
}
export function draftKey(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && new TextEncoder().encode(value).length <= 512 && !/[\x00-\x1f\x7f]/.test(value)
}
/** A reserved non-dirty draft of a tab with Settings open: the address under the
 * sheet, which recovery reopens with the sheet closed. Never an editor's key. */
export const SETTINGS_BACKGROUND = 'workspace.settingsBackground'
/** The address a SETTINGS_BACKGROUND value names, checked as any tab address; never Settings itself. */
export function settingsBackground(value: string): string | null {
  try {
    const path = safePath(JSON.parse(value))
    return path && !isSheet(path.split(/[?#]/)[0] || '/') ? path : null
  } catch { return null }
}
/** Treat even the native memory snapshot as input: it contains no authority. */
export function parseRecovery(value: unknown): WorkspaceRecovery | null {
  if (!record(value) || value.version !== 1 || typeof value.identity !== 'string' || !value.identity || new TextEncoder().encode(value.identity).length > 2000 || !record(value.state) || !Array.isArray(value.drafts) || value.drafts.length > MAX_DRAFTS) return null
  const state = value.state
  if (state.version !== 1 || !Number.isSafeInteger(state.context) || (state.context as number) < 0 || !Array.isArray(state.panes) || state.panes.length < 1 || state.panes.length > 2 || !Number.isInteger(state.inUse) || (state.inUse as number) < 0 || (state.inUse as number) >= state.panes.length) return null
  if (state.share !== null && (typeof state.share !== 'number' || !Number.isFinite(state.share) || state.share < .1 || state.share > .9)) return null
  const ids = new Set<string>()
  for (const pane of state.panes) {
    if (!record(pane) || !Array.isArray(pane.tabs) || !pane.tabs.length || pane.tabs.length > MAX_TABS || !id(pane.front)) return null
    const row = new Set<string>()
    for (const tab of pane.tabs) {
      if (!record(tab) || !id(tab.id) || ids.has(tab.id) || !safePath(tab.path) || typeof tab.title !== 'string' || tab.title.length > 512 || ['unsaved', 'blank', 'canGoBack', 'canGoForward'].some(field => typeof tab[field] !== 'boolean')) return null
      ids.add(tab.id); row.add(tab.id)
    }
    if (!row.has(pane.front)) return null
  }
  const keys = new Set<string>()
  for (const draft of value.drafts) {
    if (!record(draft) || !id(draft.tab) || !ids.has(draft.tab) || !safePath(draft.path) || !draftKey(draft.key) || typeof draft.dirty !== 'boolean' || typeof draft.value !== 'string' || new TextEncoder().encode(draft.value).length > MAX_DRAFT_BYTES) return null
    const key = JSON.stringify([draft.tab, draft.path, draft.key])
    if (keys.has(key)) return null
    keys.add(key)
    try { JSON.parse(draft.value) } catch { return null }
  }
  try { if (new TextEncoder().encode(JSON.stringify(value)).length > MAX_RECOVERY_BYTES) return null } catch { return null }
  return value as unknown as WorkspaceRecovery
}
export function recoveryBootstrap(text: string | null): WorkspaceRecovery | null {
  if (!text || text.length > MAX_RECOVERY_BYTES) return null
  try {
    const value: unknown = JSON.parse(text)
    return record(value) && value.version === 1 ? parseRecovery(value.recovery) : null
  } catch { return null }
}
export function command(value: unknown): WorkspaceCommand | null {
  if (!record(value)) return null
  switch (value.type) {
    case 'open': {
      const path = safePath(value.path)
      if (!path || !['external', 'current', 'background', 'foreground', 'other-pane'].includes(String(value.disposition)) || (value.source !== undefined && !id(value.source))) return null
      return { type: 'open', path, disposition: value.disposition as Extract<WorkspaceCommand, { type: 'open' }>['disposition'], ...(value.source === undefined ? {} : { source: value.source as string }) }
    }
    case 'cycle': return Number.isSafeInteger(value.step) && Math.abs(value.step as number) <= MAX_TABS ? { type: 'cycle', step: value.step as number } : null
    case 'move-other': return value.tab === undefined || id(value.tab) ? { type: 'move-other', ...(value.tab === undefined ? {} : { tab: value.tab as string }) } : null
    case 'close-tab': case 'back': case 'forward': case 'reload': case 'settings': return value.source === undefined || id(value.source) ? { type: value.type, ...(value.source === undefined ? {} : { source: value.source as string }) } : null
    case 'new-tab': case 'split': return { type: value.type }
    default: return null
  }
}
